import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import AdSense from '../components/AdSense';
import ScripSelector from '../components/ScripSelector';
import CombinedChart from '../components/CombinedChart';
import { fetchSymbols, fetchIVAnalysis, fetchScripAnalysis, fetchGBMBands, fetchMaxPainChain } from '../utils/api';
import GBMChart from '../components/GBMChart';
import MaxPainCharts from '../components/MaxPainCharts';
import BlogLink from '../components/BlogLink';
import { getInterpretation } from '../utils/calculations';
import { useDarkMode } from '../context/DarkModeContext';

function Card({ children, className = '' }) {
  return (
    <div className={`bg-surface border border-surface rounded-clay p-4 shadow-clay dark:shadow-clay-dark ${className}`}>
      {children}
    </div>
  );
}

function PanelLabel({ children }) {
  return (
    <p className="text-xs font-bold uppercase tracking-widest text-neutral-400 dark:text-neutral-500 mb-1">
      {children}
    </p>
  );
}

export default function ScripAnalysis() {
  const { dark: isDark } = useDarkMode();

  const [symbols, setSymbols] = useState(null);
  const [symbolsLoading, setSymbolsLoading] = useState(true);
  const [selectedSymbol, setSelectedSymbol] = useState('NIFTY');
  const [selectedDate, setSelectedDate] = useState('');
  const [feature1, setFeature1] = useState(true);   // Max Pain visible by default
  const [feature2, setFeature2] = useState(false);  // S/R Lines hidden by default
  const [ivAnalysis, setIvAnalysis] = useState(null);
  const [ivLoading, setIvLoading] = useState(false);
  const [scripData, setScripData] = useState(null);
  const [scripLoading, setScripLoading] = useState(false);
  const [gbmData, setGbmData] = useState(null);
  const [mpChainData, setMpChainData] = useState(null);
  const [mpChainLoading, setMpChainLoading] = useState(false);
  const [mpChainError, setMpChainError] = useState(null);


  useEffect(() => {
    fetchSymbols()
      .then(setSymbols)
      .catch(() => {})
      .finally(() => setSymbolsLoading(false));

    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    setSelectedDate(`${yyyy}-${mm}-${dd}`);
  }, []);

  // Fetch IV analysis whenever symbol or date changes.
  // If the selected date has no data (holiday/weekend), fall back to the
  // most recent available trading day (fetch without a date param).
  useEffect(() => {
    if (!selectedSymbol) { setIvAnalysis(null); return; }
    setIvLoading(true);
    fetchIVAnalysis(selectedSymbol, selectedDate || null)
      .then(setIvAnalysis)
      .catch(() => {
        // Date-specific fetch failed — retry without a date to get the latest data
        if (selectedDate) {
          return fetchIVAnalysis(selectedSymbol, null)
            .then(setIvAnalysis)
            .catch(() => setIvAnalysis(null));
        }
        setIvAnalysis(null);
      })
      .finally(() => setIvLoading(false));
  }, [selectedSymbol, selectedDate]);

  // Fetch OHLC + max pain chart data whenever symbol changes
  useEffect(() => {
    if (!selectedSymbol) { setScripData(null); return; }
    setScripLoading(true);
    fetchScripAnalysis(selectedSymbol, 90)
      .then(setScripData)
      .catch(() => setScripData(null))
      .finally(() => setScripLoading(false));
  }, [selectedSymbol]);

  // Fetch GBM bands whenever symbol changes
  useEffect(() => {
    if (!selectedSymbol) { setGbmData(null); return; }
    fetchGBMBands(selectedSymbol).then(setGbmData).catch(() => setGbmData(null));
  }, [selectedSymbol]);

  // Fetch max pain option chain whenever symbol changes
  useEffect(() => {
    if (!selectedSymbol) { setMpChainData(null); setMpChainError(null); return; }
    setMpChainLoading(true);
    setMpChainError(null);
    fetchMaxPainChain(selectedSymbol)
      .then((d) => { setMpChainData(d); setMpChainError(null); })
      .catch(() => { setMpChainData(null); setMpChainError('no_data'); })
      .finally(() => setMpChainLoading(false));
  }, [selectedSymbol]);

  // Previous trading day relative to selectedDate (for % change + pivot points)
  const prevDayData = useMemo(() => {
    const rows = scripData?.data;
    if (!rows?.length) return null;
    if (selectedDate) {
      const idx = rows.findIndex((d) => d.date === selectedDate);
      if (idx > 0) return rows[idx - 1];
    }
    // Fallback: second-to-last entry
    return rows.length >= 2 ? rows[rows.length - 2] : null;
  }, [scripData, selectedDate]);

  // % change vs previous day's close
  const pctChange = useMemo(() => {
    const spot = ivAnalysis?.spot_price;
    const prev = prevDayData?.close;
    if (!spot || !prev) return null;
    return ((Number(spot) - Number(prev)) / Number(prev)) * 100;
  }, [ivAnalysis, prevDayData]);

  // Standard pivot points from previous day's H/L/C
  const pivots = useMemo(() => {
    const H = prevDayData?.high;
    const L = prevDayData?.low;
    const C = prevDayData?.close;
    if (!H || !L || !C) return null;
    const P = (Number(H) + Number(L) + Number(C)) / 3;
    const fix = (n) => +n.toFixed(2);
    return {
      r3: fix(Number(H) + 2 * (P - Number(L))),
      r2: fix(P + Number(H) - Number(L)),
      r1: fix(2 * P - Number(L)),
      s1: fix(2 * P - Number(H)),
      s2: fix(P - Number(H) + Number(L)),
      s3: fix(Number(L) - 2 * (Number(H) - P)),
    };
  }, [prevDayData]);

  return (
    <>
      <SEOHead
        canonicalPath="/scrip-analysis"
        title="Scrip Analysis — Technical & Options Analysis | OptionView"
        description="In-depth technical and options analysis for NSE F&O symbols. OHLC chart, moving averages, RSI, max pain, support & resistance levels."
        keywords="scrip analysis India, NIFTY technical analysis, options max pain, support resistance, IV chart NSE"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4">
        <Link to="/" className="inline-flex items-center gap-1 text-xs text-neutral-400 dark:text-neutral-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors mb-2">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
          Back to Home
        </Link>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-4">

        {/* Page title */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900 dark:text-white inline-flex items-baseline gap-3">
            Scrip Analysis
            <span className="text-sm font-semibold text-red-500 tracking-normal">Work In Progress</span>
          </h1>
          <p className="text-neutral-500 dark:text-neutral-400 mt-1 text-sm">
            Technical and options analysis for NSE F&amp;O symbols — OHLC chart, moving averages, RSI, max pain, and support &amp; resistance levels.
          </p>
        </div>

        {/* Controls card */}
        <Card>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
            <ScripSelector
              symbols={symbols}
              selectedSymbol={selectedSymbol}
              onSymbolChange={setSelectedSymbol}
              loading={symbolsLoading}
            />
            <div>
              <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">Date</label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="input-base focus-ring h-9"
              />
            </div>
          </div>
        </Card>

        {/* ── Component: OHLC Chart ─────────────────────────────────────────── */}

        {/* Section header row — transparent */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-neutral-900 dark:text-neutral-100">
            <span className="text-neutral-400 dark:text-neutral-500 font-bold">(1/5)</span>{' '}
            OHLC Chart
          </h2>
          <div className="flex items-center gap-5">
            <label className="flex items-center gap-1.5 text-xs text-neutral-500 dark:text-neutral-400 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={feature1}
                onChange={(e) => setFeature1(e.target.checked)}
                className="w-3.5 h-3.5 rounded accent-primary-600 cursor-pointer"
              />
              Max Pain
            </label>
            <label className="flex items-center gap-1.5 text-xs text-neutral-500 dark:text-neutral-400 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={feature2}
                onChange={(e) => setFeature2(e.target.checked)}
                className="w-3.5 h-3.5 rounded accent-primary-600 cursor-pointer"
              />
              S/R Lines (experimental)
            </label>
          </div>
        </div>

        {/* Main 2-column layout */}
        <div className="flex gap-4" style={{ minHeight: 560 }}>

          {/* Left column — 1/4 width, full height, stats panel */}
          <div className="w-1/4 flex-shrink-0">
            <Card className="h-full flex flex-col gap-5 !p-5">

              {/* Spot Price */}
              <div>
                <PanelLabel>Spot Price</PanelLabel>
                {ivLoading ? (
                  <div className="space-y-1.5 mt-1">
                    <div className="h-8 w-28 rounded bg-neutral-100 dark:bg-white/5 animate-pulse" />
                    <div className="h-4 w-16 rounded bg-neutral-100 dark:bg-white/5 animate-pulse" />
                  </div>
                ) : (
                  <>
                    <p className="text-2xl font-bold text-neutral-800 dark:text-neutral-100 font-mono">
                      {ivAnalysis?.spot_price
                        ? Number(ivAnalysis.spot_price).toLocaleString('en-IN', { maximumFractionDigits: 2 })
                        : '—'}
                    </p>
                    {pctChange != null && (
                      <p className={`text-xs font-semibold font-mono mt-0.5 ${pctChange >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {pctChange >= 0 ? '▲' : '▼'} {Math.abs(pctChange).toFixed(2)}%
                      </p>
                    )}
                  </>
                )}
              </div>

              <div className="border-t border-neutral-200 dark:border-white/8" />

              {/* S&R section header */}
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-neutral-400 dark:text-neutral-500">
                  Support &amp; Resistance
                  <span className="ml-1 normal-case tracking-normal font-normal text-neutral-300 dark:text-neutral-600">(experimental)</span>
                </p>
              </div>

              {/* Resistance */}
              <div>
                <PanelLabel>Resistance</PanelLabel>
                <div className="space-y-1 mt-2">
                  {[
                    { label: 'R3', value: pivots?.r3 },
                    { label: 'R2', value: pivots?.r2 },
                    { label: 'R1', value: pivots?.r1 },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex items-center justify-between text-xs">
                      <span className="text-red-400 dark:text-red-400 font-medium">{label}</span>
                      <span className="text-neutral-700 dark:text-neutral-300 font-mono">
                        {value != null
                          ? value.toLocaleString('en-IN', { maximumFractionDigits: 2 })
                          : '—'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-neutral-200 dark:border-white/8" />

              {/* Support */}
              <div>
                <PanelLabel>Support</PanelLabel>
                <div className="space-y-1 mt-2">
                  {[
                    { label: 'S1', value: pivots?.s1 },
                    { label: 'S2', value: pivots?.s2 },
                    { label: 'S3', value: pivots?.s3 },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex items-center justify-between text-xs">
                      <span className="text-emerald-500 dark:text-emerald-400 font-medium">{label}</span>
                      <span className="text-neutral-700 dark:text-neutral-300 font-mono">
                        {value != null
                          ? value.toLocaleString('en-IN', { maximumFractionDigits: 2 })
                          : '—'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-neutral-200 dark:border-white/8" />

              {/* Historical vs Implied Volatility */}
              <div className="flex-1 flex flex-col gap-3">
                <PanelLabel>Historical vs. Implied Volatility</PanelLabel>

                {ivLoading ? (
                  <div className="grid grid-cols-3 gap-1.5">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div key={i} className="h-12 rounded-clay-sm bg-neutral-100 dark:bg-white/5 animate-pulse" />
                    ))}
                  </div>
                ) : (
                  <>
                    {/* 3×2 stat cards */}
                    <div className="grid grid-cols-3 gap-1.5">

                      {/* Card 1: IV + HV */}
                      <div className="rounded-clay-sm bg-neutral-50 dark:bg-white/5 border border-neutral-200 dark:border-white/8 p-2 text-center col-span-1">
                        <p className="text-[9px] text-neutral-400 dark:text-neutral-500 uppercase tracking-wide leading-none mb-1">IV / HV</p>
                        <p className="text-sm font-bold text-neutral-800 dark:text-neutral-100 font-mono leading-tight">
                          {ivAnalysis ? `${ivAnalysis.iv_percent}%` : '—'}
                        </p>
                        <p className="text-[9px] text-neutral-400 dark:text-neutral-500 font-mono mt-0.5">
                          HV —
                        </p>
                      </div>

                      {/* Card 2: IV Rank */}
                      <div className="rounded-clay-sm bg-neutral-50 dark:bg-white/5 border border-neutral-200 dark:border-white/8 p-2 text-center">
                        <p className="text-[9px] text-neutral-400 dark:text-neutral-500 uppercase tracking-wide leading-none mb-1">IV Rank</p>
                        <p className="text-sm font-bold text-neutral-800 dark:text-neutral-100 font-mono">
                          {ivAnalysis?.iv_rank != null ? ivAnalysis.iv_rank : '—'}
                        </p>
                        {ivAnalysis?.iv_rank != null && (
                          <div className="mt-1 h-0.5 bg-neutral-200 dark:bg-white/10 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${ivAnalysis.iv_rank > 70 ? 'bg-orange-500' : ivAnalysis.iv_rank < 30 ? 'bg-green-500' : 'bg-primary-500'}`}
                              style={{ width: `${ivAnalysis.iv_rank}%` }}
                            />
                          </div>
                        )}
                      </div>

                      {/* Card 3: IV Percentile */}
                      <div className="rounded-clay-sm bg-neutral-50 dark:bg-white/5 border border-neutral-200 dark:border-white/8 p-2 text-center">
                        <p className="text-[9px] text-neutral-400 dark:text-neutral-500 uppercase tracking-wide leading-none mb-1">IV Pctl</p>
                        <p className="text-sm font-bold text-neutral-800 dark:text-neutral-100 font-mono">
                          {ivAnalysis?.iv_percentile != null ? `${ivAnalysis.iv_percentile}` : '—'}
                        </p>
                        {ivAnalysis?.iv_percentile != null && (
                          <p className="text-[9px] text-neutral-400 dark:text-neutral-500">th</p>
                        )}
                      </div>

                      {/* Cards 4–6: Historical stats */}
                      {[
                        { label: 'Min IV', value: ivAnalysis?.historical_stats?.min_iv },
                        { label: 'Avg IV', value: ivAnalysis?.historical_stats?.avg_iv },
                        { label: 'Max IV', value: ivAnalysis?.historical_stats?.max_iv },
                      ].map(({ label, value }) => (
                        <div key={label} className="rounded-clay-sm bg-neutral-50 dark:bg-white/5 border border-neutral-200 dark:border-white/8 p-2 text-center">
                          <p className="text-[9px] text-neutral-400 dark:text-neutral-500 uppercase tracking-wide leading-none mb-1">{label}</p>
                          <p className="text-sm font-bold text-neutral-800 dark:text-neutral-100 font-mono">
                            {value != null ? `${value.toFixed(1)}%` : '—'}
                          </p>
                        </div>
                      ))}
                    </div>

                    {/* Synopsis card — full width interpretation */}
                    <div className="rounded-clay-sm border border-neutral-200 dark:border-white/8 bg-neutral-50 dark:bg-white/5 px-3 py-2">
                      {ivAnalysis ? (() => {
                        const interp = getInterpretation(ivAnalysis.iv_rank, ivAnalysis.iv_percentile);
                        return (
                          <>
                            <p className={`text-xs font-semibold ${interp.color}`}>{interp.text}</p>
                            {interp.recommendation && (
                              <p className="text-[10px] text-neutral-500 dark:text-neutral-400 mt-0.5 leading-snug">
                                {interp.recommendation}
                              </p>
                            )}
                          </>
                        );
                      })() : (
                        <p className="text-xs text-neutral-400 dark:text-neutral-500">
                          Select a symbol to see IV interpretation
                        </p>
                      )}
                    </div>
                  </>
                )}
              </div>

            </Card>
          </div>

          {/* Right column — 3/4 width, single combined chart card */}
          <Card className="flex-1 !p-2 overflow-hidden">
            {scripLoading && !scripData ? (
              <div className="w-full h-full animate-pulse bg-neutral-100 dark:bg-white/5" />
            ) : (
              <CombinedChart
                data={scripData?.data}
                isIndex={scripData?.is_index ?? true}
                isDark={isDark}
                showMaxPain={feature1}
                showSR={feature2}
                pivots={pivots}
              />
            )}
          </Card>
        </div>

        {/* Blog links row */}
        <div className="grid grid-cols-2 gap-3">
          <BlogLink to="/blog/max-pain/what-is-max-pain" tooltip="The strike price where options writers collectively pay out the least — markets tend to gravitate toward it on expiry day.">What is Max Pain?</BlogLink>
          <BlogLink to="/blog/technical-analysis/support-resistance">Support &amp; Resistance Identification</BlogLink>
        </div>

      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <AdSense adSlot="" label="Scrip Analysis Banner" adStyle={{ minHeight: 90 }} />
      </div>

      {/* ── (2/5) Stochastic Analysis ──────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-10 space-y-4">

        {/* Section header */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-neutral-900 dark:text-neutral-100">
            <span className="text-neutral-400 dark:text-neutral-500 font-bold">(2/5)</span>{' '}
            Stochastic Analysis
          </h2>
          {/* Feature toggles — placeholder, not yet wired
          <div className="flex items-center gap-5">
            <label className="flex items-center gap-1.5 text-xs text-neutral-500 dark:text-neutral-400 cursor-pointer select-none">
              <input type="checkbox" className="w-3.5 h-3.5 rounded accent-primary-600 cursor-pointer" />
              Feature 1
            </label>
            <label className="flex items-center gap-1.5 text-xs text-neutral-500 dark:text-neutral-400 cursor-pointer select-none">
              <input type="checkbox" className="w-3.5 h-3.5 rounded accent-primary-600 cursor-pointer" />
              Feature 2
            </label>
          </div>
          */}
        </div>

        {/* Row 1 — 3 equal columns, taller */}
        <div className="grid grid-cols-3 gap-4" style={{ minHeight: 320 }}>

          {/* Col 1: GBM fan chart */}
          <Card className="!p-0 overflow-hidden flex flex-col">
            <div className="p-3 pb-1">
              <p className="text-xs font-bold uppercase tracking-widest text-neutral-400 dark:text-neutral-500">
                Price Distribution — GBM
              </p>
              {gbmData && (
                <p className="text-[10px] text-neutral-400 dark:text-neutral-500 mt-0.5">
                  Expiry {gbmData.expiry_date} · {gbmData.days_to_expiry} trading day{gbmData.days_to_expiry !== 1 ? 's' : ''}
                </p>
              )}
            </div>
            <div className="flex-1 min-h-0 px-2 pb-2">
              {gbmData ? (
                <GBMChart bands={gbmData.bands} s0={gbmData.s0} isDark={isDark} />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <p className="text-xs text-neutral-300 dark:text-neutral-600">
                    {selectedSymbol ? 'Loading…' : 'Select a symbol'}
                  </p>
                </div>
              )}
            </div>
          </Card>

          {/* Col 2: Numerical confidence values */}
          <Card className="flex flex-col gap-3">
            <p className="text-xs font-bold uppercase tracking-widest text-neutral-400 dark:text-neutral-500">
              Confidence Intervals
            </p>
            <div className="flex-1 space-y-2">
              {(() => {
                const last = gbmData?.bands?.[gbmData.bands.length - 1];
                return [
                  { label: '±1σ  (68%)', color: 'text-indigo-400', upper: last?.upper1, lower: last?.lower1 },
                  { label: '±2σ  (95%)', color: 'text-indigo-300', upper: last?.upper2, lower: last?.lower2 },
                  { label: '±3σ  (99.7%)', color: 'text-indigo-200', upper: last?.upper3, lower: last?.lower3 },
                ].map(({ label, color, upper, lower }) => (
                  <div key={label} className="flex items-center justify-between text-xs">
                    <span className={`font-medium font-mono ${color}`}>{label}</span>
                    <div className="flex gap-3 text-neutral-400 dark:text-neutral-500 font-mono">
                      <span>↑ {upper != null ? upper.toLocaleString('en-IN', { maximumFractionDigits: 0 }) : '—'}</span>
                      <span>↓ {lower != null ? lower.toLocaleString('en-IN', { maximumFractionDigits: 0 }) : '—'}</span>
                    </div>
                  </div>
                ));
              })()}
            </div>
            <div className="mt-auto pt-2 border-t border-neutral-200 dark:border-white/8">
              <p className="text-[10px] text-neutral-300 dark:text-neutral-600">
                Expected path at expiry:{' '}
                {gbmData?.bands?.length
                  ? gbmData.bands[gbmData.bands.length - 1].expected.toLocaleString('en-IN', { maximumFractionDigits: 0 })
                  : '—'}
              </p>
            </div>
          </Card>

          {/* Col 3: Empty placeholder */}
          <Card className="flex items-center justify-center">
            <p className="text-xs text-neutral-300 dark:text-neutral-600">—</p>
          </Card>

        </div>

        {/* Row 2 — 3 equal columns, shorter */}
        <div className="grid grid-cols-3 gap-4">

          {/* Col 1: Drift */}
          <Card className="flex flex-col gap-2">
            <p className="text-xs font-bold uppercase tracking-widest text-neutral-400 dark:text-neutral-500">
              Drift (μ)
            </p>
            <p className={`text-xl font-bold font-mono ${gbmData ? (gbmData.mu >= 0 ? 'text-emerald-500' : 'text-red-500') : 'text-neutral-800 dark:text-neutral-100'}`}>
              {gbmData
                ? `${gbmData.mu >= 0 ? '+' : ''}${gbmData.mu.toFixed(2)}%`
                : '—'}
            </p>
            <p className="text-[10px] text-neutral-400 dark:text-neutral-500">annualised drift rate</p>
          </Card>

          {/* Col 2: Standard Deviation */}
          <Card className="flex flex-col gap-2">
            <p className="text-xs font-bold uppercase tracking-widest text-neutral-400 dark:text-neutral-500">
              Implied Volatility (σ)
            </p>
            <p className="text-xl font-bold font-mono text-neutral-800 dark:text-neutral-100">
              {gbmData ? `${gbmData.sigma.toFixed(2)}%` : '—'}
            </p>
            <p className="text-[10px] text-neutral-400 dark:text-neutral-500">annualised implied volatility</p>
          </Card>

          {/* Col 3: Blog link */}
          <Card className="flex flex-col justify-between">
            <p className="text-xs font-bold uppercase tracking-widest text-neutral-400 dark:text-neutral-500">
              Learn More
            </p>
            <div className="flex-1 flex flex-col justify-center gap-1">
              <p className="text-xs text-neutral-500 dark:text-neutral-400 leading-snug">
                Understanding Geometric Brownian Motion and the Black-Scholes PDE.
              </p>
            </div>
            <a
              href="#"
              className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline mt-2"
            >
              Read the PDE blog post
              <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </a>
          </Card>

        </div>
      </div>

      {/* ── (3/5) Max Pain ─────────────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-10 space-y-4">

        {/* Section header */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-neutral-900 dark:text-neutral-100">
            <span className="text-neutral-400 dark:text-neutral-500 font-bold">(3/5)</span>{' '}
            Max Pain
          </h2>
        </div>

        {/* Main card */}
        <Card>
          {mpChainLoading ? (
            <div className="w-full h-48 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : mpChainError || !mpChainData ? (
            <div className="w-full py-10 flex flex-col items-center gap-2">
              <p className="text-sm text-neutral-400 dark:text-neutral-500">
                Option chain data not available for <span className="font-semibold">{selectedSymbol}</span>.
              </p>
              <p className="text-xs text-neutral-300 dark:text-neutral-600">
                Run <code className="font-mono px-1 py-0.5 bg-neutral-100 dark:bg-white/5 rounded">backfill_option_chain</code> on the server to populate this section.
              </p>
            </div>
          ) : (
            <>
              {/* Stats bar */}
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-4 pb-3 border-b border-neutral-200 dark:border-white/8">
                <div>
                  <PanelLabel>Data as of</PanelLabel>
                  <p className="text-xs font-mono text-neutral-700 dark:text-neutral-300">{mpChainData.trade_date}</p>
                </div>
                <div>
                  <PanelLabel>Expiry</PanelLabel>
                  <p className="text-xs font-mono text-neutral-700 dark:text-neutral-300">{mpChainData.expiry_date}</p>
                </div>
                <div>
                  <PanelLabel>Spot</PanelLabel>
                  <p className="text-xs font-mono font-semibold text-blue-500">
                    ₹{mpChainData.spot_price != null
                      ? Number(mpChainData.spot_price).toLocaleString('en-IN', { maximumFractionDigits: 0 })
                      : '—'}
                  </p>
                </div>
                <div>
                  <PanelLabel>Max Pain</PanelLabel>
                  <p className="text-xs font-mono font-semibold text-amber-500">
                    ₹{Number(mpChainData.max_pain).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </p>
                </div>
                <div>
                  <PanelLabel>PCR</PanelLabel>
                  <p className={`text-xs font-mono font-semibold ${
                    mpChainData.pcr > 1.3 ? 'text-red-500'
                    : mpChainData.pcr < 0.7 ? 'text-emerald-500'
                    : 'text-neutral-700 dark:text-neutral-300'
                  }`}>
                    {mpChainData.pcr.toFixed(2)}{' '}
                    <span className="font-normal text-neutral-400 dark:text-neutral-500">
                      {mpChainData.pcr > 1.3 ? 'Bearish' : mpChainData.pcr < 0.7 ? 'Bullish' : 'Neutral'}
                    </span>
                  </p>
                </div>
                {mpChainData.below_pins?.length > 0 && (
                  <div>
                    <PanelLabel>↓ Pins</PanelLabel>
                    <p className="text-xs font-mono text-pink-500">
                      {mpChainData.below_pins.map((s) =>
                        `₹${Number(s).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
                      ).join('  ')}
                    </p>
                  </div>
                )}
                {mpChainData.above_pins?.length > 0 && (
                  <div>
                    <PanelLabel>↑ Pins</PanelLabel>
                    <p className="text-xs font-mono text-cyan-500">
                      {mpChainData.above_pins.map((s) =>
                        `₹${Number(s).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
                      ).join('  ')}
                    </p>
                  </div>
                )}
              </div>

              {/* The three charts */}
              <MaxPainCharts data={mpChainData} isDark={isDark} />

              {/* Blog link row */}
              <div className="mt-4 pt-3 border-t border-neutral-200 dark:border-white/8">
                <BlogLink to="/blog/max-pain/what-is-max-pain" dark>What is Max Pain?</BlogLink>
              </div>
            </>
          )}
        </Card>

      </div>
    </>
  );
}
